import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { FormField, Knowledge, MultiRowOptions } from '../../types/form';
import { Plus, X } from 'lucide-react';

interface MultiRowFieldEditorProps {
  field: FormField;
  editedField: FormField;
  onFieldChange: (updates: Partial<FormField>) => void;
  knowledge: Knowledge[];
}

export const MultiRowFieldEditor: React.FC<MultiRowFieldEditorProps> = ({
  field,
  editedField,
  onFieldChange,
  knowledge,
}) => {
  const options = (editedField.options || {}) as MultiRowOptions;

  const updateOptions = (updates: any) => {
    onFieldChange({
      options: {
        ...editedField.options,
        ...updates
      }
    });
  };

  const columns = options.columns || [];
  const rowLabels = options.rowLabels || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id={`allowAddRows-${field.id}`}
          checked={options.allowAddRows !== false}
          onChange={(e) => updateOptions({ allowAddRows: e.target.checked })}
          className="rounded"
        />
        <Label htmlFor={`allowAddRows-${field.id}`}>Allow users to add new rows</Label>
      </div>

      <div>
        <Label htmlFor={`initialRowCount-${field.id}`}>Initial Row Count</Label>
        <Input
          id={`initialRowCount-${field.id}`}
          type="number"
          min="1"
          max="20"
          value={options.initialRowCount || 1}
          onChange={(e) => updateOptions({ initialRowCount: parseInt(e.target.value) || 3 })}
          className="w-24"
        />
      </div>

      <div>
        <Label>Row Labels (optional)</Label>
        <div className="space-y-2">
          {rowLabels.map((label: string, index: number) => (
            <div key={index} className="flex items-center space-x-2">
              <Input
                value={label}
                onChange={(e) => {
                  const newLabels = [...rowLabels];
                  newLabels[index] = e.target.value;
                  updateOptions({ rowLabels: newLabels });
                }}
                placeholder={`Row ${index + 1} label`}
                className="flex-1"
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  const newLabels = rowLabels.filter((_: string, i: number) => i !== index);
                  updateOptions({ rowLabels: newLabels });
                }}
                className="h-8 w-8 p-0"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              updateOptions({ rowLabels: [...rowLabels, ''] });
            }}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Row Label
          </Button>
        </div>
      </div>

      <div>
        <Label>Configure Columns</Label>
        <div className="space-y-2">
          {columns.map((column: any, index: number) => (
            <div key={index} className="space-y-2 p-3 border rounded-lg">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Input
                    value={column.label || ''}
                    onChange={(e) => {
                      const newColumns = [...columns];
                      newColumns[index] = { ...column, label: e.target.value };
                      updateOptions({ columns: newColumns });
                    }}
                    placeholder="Column label"
                    className="flex-1"
                  />
                  <Select
                    value={column.type || 'text'}
                    onValueChange={(value) => {
                      const newColumns = [...columns];
                      newColumns[index] = { ...column, type: value };
                      updateOptions({ columns: newColumns });
                    }}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="text">Text</SelectItem>
                      <SelectItem value="number">Number</SelectItem>
                      <SelectItem value="phone">Phone</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="dropdown">Dropdown</SelectItem>
                      <SelectItem value="date">Date</SelectItem>
                      <SelectItem value="file">File</SelectItem>
                      <SelectItem value="checkbox">Checkbox</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      const newColumns = columns.filter((_: any, i: number) => i !== index);
                      updateOptions({ columns: newColumns });
                    }}
                    className="h-8 w-8 p-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id={`columnRequired-${field.id}-${index}`}
                    checked={column.required || false}
                    onChange={(e) => {
                      const newColumns = [...columns];
                      newColumns[index] = { ...column, required: e.target.checked };
                      updateOptions({ columns: newColumns });
                    }}
                    className="rounded"
                  />
                  <Label htmlFor={`columnRequired-${field.id}-${index}`} className="text-sm">
                    Required
                  </Label>
                </div>
              </div>

              {(column.type === 'dropdown' || column.type === 'checkbox') && (
                <div className="space-y-2">
                  <Label className="text-sm">{column.type === 'dropdown' ? 'Dropdown' : 'Checkbox'} Options</Label>
                  <Select
                    value={column.knowledge || 'custom'}
                    onValueChange={(value) => {
                      const newColumns = [...columns];
                      if (value === 'custom') {
                        newColumns[index] = { ...column, knowledge: undefined, options: [] };
                      } else {
                        const selectedKnowledge = knowledge.find(k => k.id === value);
                        if (selectedKnowledge) {
                          newColumns[index] = { 
                            ...column, 
                            knowledge: value,
                            options: Object.values(selectedKnowledge.options)
                          };
                        }
                      }
                      updateOptions({ columns: newColumns });
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select knowledge or create custom options" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom Options</SelectItem>
                      {knowledge?.map(k => (
                        <SelectItem key={k.id} value={k.id}>
                          {k.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {!column.knowledge && (
                    <div className="space-y-1">
                      {(column.options || []).map((option: string, optionIndex: number) => (
                        <div key={optionIndex} className="flex items-center space-x-2">
                          <Input
                            value={option}
                            onChange={(e) => {
                              const newColumns = [...columns];
                              const newOptions = [...(column.options || [])];
                              newOptions[optionIndex] = e.target.value;
                              newColumns[index] = { ...column, options: newOptions };
                              updateOptions({ columns: newColumns });
                            }}
                            placeholder={`Option ${optionIndex + 1}`}
                            className="text-sm"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const newColumns = [...columns];
                              const newOptions = (column.options || []).filter((_: string, i: number) => i !== optionIndex);
                              newColumns[index] = { ...column, options: newOptions };
                              updateOptions({ columns: newColumns });
                            }}
                            className="h-6 w-6 p-0"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const newColumns = [...columns];
                          const newOptions = [...(column.options || []), ''];
                          newColumns[index] = { ...column, options: newOptions };
                          updateOptions({ columns: newColumns });
                        }}
                        className="text-xs h-6"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Option
                      </Button>
                    </div>
                  )}

                  {column.knowledge && (
                    <div className="text-sm text-gray-600">
                      Using knowledge: {knowledge.find(k => k.id === column.knowledge)?.name}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const newColumn = { 
                id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`, 
                label: '', 
                type: 'text' as const
              };
              updateOptions({ columns: [...columns, newColumn] });
            }}
            className="w-full"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Column
          </Button>
        </div>
      </div>
    </div>
  );
};
